"use client";
import path from "node:path";
import type { ClinicalProviderFormData } from "@/app/directories/checkout/components/intake/IntakeClinicalProviderInput";
import type { IntakeQuestionChoice } from "@/app/directories/checkout/components/intake/IntakeForm";
import type { IntakeListEntry } from "@/app/directories/checkout/components/intake/IntakeListInput";
import type { LanguageCode } from "@frontend/lib/graphql/generated-gql";
import type { ConsumerData } from "@frontend/lib/schemas/clientSchema";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from "@react-pdf/renderer";
import { differenceInYears, format, parse, parseISO } from "date-fns";

// Client: bare URL resolves against the origin to `public/fonts/` on the CDN.
// Server: `@react-pdf` treats src as a filesystem path, so bare `/fonts/...`
// would ENOENT on the Vercel lambda. Point at the route-colocated fonts
// directory instead — `@vercel/nft` already bundles it via the submit-intake-pdf
// route (see PR #4385).
const FONT_BASE =
  typeof window !== "undefined"
    ? "/fonts"
    : path.join(process.cwd(), "app/api/submit-intake-pdf/fonts");

Font.register({
  family: "Suisse",
  fonts: [
    {
      src: `${FONT_BASE}/SuisseIntl-Regular.woff`,
    },
    {
      src: `${FONT_BASE}/SuisseIntl-SemiBold.woff`,
      fontWeight: 600,
    },
    {
      src: `${FONT_BASE}/SuisseIntl-Light.woff`,
      fontWeight: 300,
    },
    {
      src: `${FONT_BASE}/SuisseIntl-Medium.woff`,
      fontWeight: 500,
    },
  ],
});

Font.register({
  family: "madi",
  src: `${FONT_BASE}/MsMadi-Regular.ttf`,
});

Font.register({
  family: "vujahday",
  src: `${FONT_BASE}/VujahdayScript-Regular.ttf`,
});

Font.register({
  family: "delafield",
  src: `${FONT_BASE}/MrsSaintDelafield-Regular.ttf`,
});

Font.register({
  family: "noto",
  src: `${FONT_BASE}/NotoSansSC-VariableFont_wght.ttf`,
});

/**
 * PDF stylesheet creator
 */
const styles = StyleSheet.create({
  page: {
    padding: 45,
    fontSize: 10,
    flexDirection: "column",
    justifyContent: "flex-start",
    fontFamily: "Suisse",
    fontWeight: 300,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  metadata: {
    marginBottom: 20,
    fontSize: 10,
    fontFamily: "Suisse",
    fontWeight: 300,
  },
  clinicInfo: {
    marginBottom: 10,
    fontFamily: "Suisse",
    fontWeight: 300,
  },
  title: {
    fontSize: 20,
    marginBottom: 10,
    fontWeight: 300,
    fontFamily: "Suisse",
  },
  sectionColumnHead: {
    fontSize: 10,
    fontWeight: 400,
    fontFamily: "Suisse",
    marginTop: 5,
    marginBottom: 5,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  rowStart: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: 5,
  },
  column: {
    flexDirection: "column",
    flex: 1,
  },
  label: {
    fontWeight: "bold",
    marginRight: 10,
  },
  divider: {
    borderTopWidth: 1,
    borderColor: "#e1e1e1",
    marginBottom: 3,
    marginTop: 3,
  },
  weakDivider: {
    borderTopWidth: 1,
    borderColor: "#000",
    opacity: 0.1,
    marginVertical: 10,
  },
  verticalDivider: {
    borderLeftWidth: 1,
    borderColor: "#000",
    marginVertical: 10,
  },
  footer: {
    fontSize: 8,
    color: "#666",
  },
  pageNumber: {
    position: "absolute",
    bottom: 30,
    right: 30,
    fontSize: 8,
    color: "#666",
  },
  typedSignatureContainer: {
    width: "100%",
    flexDirection: "column",
  },
  drawnSignatureContainer: {
    width: "100%",
    flexDirection: "column",
  },
  dateContainer: {
    width: "100%",
    flexDirection: "column",
  },
  signatureLine: {
    width: "100%",
    height: 1,
    backgroundColor: "#000",
    marginTop: 0,
  },
  totalScoreLine: {
    width: "20%",
    height: 1,
    backgroundColor: "#000",
    marginTop: 18,
  },
  dateLine: {
    width: "100%",
    height: 1,
    backgroundColor: "#000",
    marginTop: 0,
  },
  signatureText: {
    fontSize: 24,
    width: "100%",
    marginLeft: 5,
    marginBottom: 0,
    lineHeight: 1,
  },
  dateText: {
    fontSize: 8,
    marginLeft: 5,
    marginBottom: 0,
  },
});

const insertBreaksForCJK = (text: string): string => {
  // Add zero-width space between every CJK character
  // Unicode ranges for CJK characters:
  // \u4E00-\u9FFF (CJK Unified Ideographs)
  // \u3400-\u4DBF (CJK Extension A)
  // \uF900-\uFAFF (CJK Compatibility Ideographs)
  return text.replace(
    /([\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF])/g,
    "$1\u200B",
  );
};

const parseHtmlToTextComponents = (
  html: string,
  useNoto: boolean,
): React.ReactElement[] => {
  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const processNode = (node: Node): React.ReactElement[] => {
    const elements: React.ReactElement[] = [];

    if (node.nodeType === Node.TEXT_NODE) {
      let text = node.textContent?.trim();
      if (text) {
        if (useNoto) {
          text = insertBreaksForCJK(text);
        }
        elements.push(
          <Text
            style={{
              fontFamily: useNoto ? "noto" : "Suisse",
              width: "100%",
            }}
            key={Math.random()}
          >
            {text}
          </Text>,
        );
      }
      return elements;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.tagName.toLowerCase()) {
        case "br":
          elements.push(
            <Text
              style={{
                fontFamily: useNoto ? "noto" : "Suisse",
                width: "100%",
              }}
              key={Math.random()}
            >
              {"\n"}
            </Text>,
          );
          break;
        case "strong":
        case "b":
          elements.push(
            <Text
              key={Math.random()}
              style={{
                fontWeight: 600,
                fontFamily: useNoto ? "noto" : "Suisse",
                width: "100%",
              }}
            >
              {Array.from(element.childNodes).flatMap(processNode)}
            </Text>,
          );
          break;
        case "em":
        case "i":
          elements.push(
            <Text
              key={Math.random()}
              style={{
                fontStyle: "normal",
                fontFamily: useNoto ? "noto" : "Suisse",
                width: "100%",
              }}
            >
              {Array.from(element.childNodes).flatMap(processNode)}
            </Text>,
          );
          break;
        case "ul":
        case "ol":
          Array.from(element.children).forEach((li, index) => {
            elements.push(
              <Text
                key={Math.random()}
                style={{
                  fontFamily: useNoto ? "noto" : "Suisse",
                  width: "100%",
                }}
              >
                {Array.from(li.childNodes).flatMap(processNode)}
              </Text>,
            );
          });
          break;
        case "p":
          elements.push(
            <Text
              key={Math.random()}
              style={{
                fontFamily: useNoto ? "noto" : "Suisse",
                width: "100%",
                fontSize: 8,
              }}
            >
              {Array.from(element.childNodes).flatMap(processNode)}
            </Text>,
          );
          break;
        default:
          elements.push(...Array.from(element.childNodes).flatMap(processNode));
      }
    }

    return elements;
  };

  return Array.from(tempDiv.childNodes).flatMap(processNode);
};

const consumerStamp = (consumer: ConsumerData) => {
  return (
    <View>
      <View style={{ display: "flex", flexDirection: "row" }}>
        <Text style={{ fontWeight: 300, fontSize: 8 }}>Name : </Text>
        <Text
          style={{ fontWeight: 300, fontSize: 8 }}
        >{`${consumer.firstname} ${consumer.lastname}`}</Text>
      </View>

      <View style={{ display: "flex", flexDirection: "row" }}>
        <Text style={{ fontWeight: 300, fontSize: 8 }}>DOB : </Text>
        <Text style={{ fontWeight: 300, fontSize: 8 }}>
          {format(
            parse(consumer.dob.substring(0, 10), "yyyy-MM-dd", new Date()),
            "MM/dd/yyyy",
          )}
        </Text>
      </View>

      <View style={{ display: "flex", flexDirection: "row" }}>
        <Text style={{ fontWeight: 300, fontSize: 8 }}>Age : </Text>
        <Text style={{ fontWeight: 300, fontSize: 8 }}>
          {differenceInYears(new Date(), parseISO(consumer.dob))}
        </Text>
      </View>
    </View>
  );
};

const PageHeader = ({
  consumer,
  formName,
  timeCreated,
}: {
  consumer: ConsumerData;
  formName: string;
  timeCreated: string | undefined;
}) => {
  return (
    <View style={styles.header} fixed>
      <View>
        <Text
          style={{
            fontSize: 24,
            fontWeight: 300,
            fontFamily: "Suisse",
            marginBottom: 5,
          }}
        >
          {formName}
        </Text>
        {timeCreated && (
          <View style={styles.clinicInfo}>{consumerStamp(consumer)}</View>
        )}
      </View>

      {timeCreated && (
        <Text style={{ fontSize: 8 }}>
          Completed :{" "}
          {format(
            timeCreated ? parseISO(timeCreated) : new Date(),
            "MM/dd/yyyy hh:mm aa",
          )}
        </Text>
      )}
    </View>
  );
};

const PageFooter = () => (
  <View style={styles.footer} fixed>
    <Text>XD</Text>
  </View>
);

const formatResponseForCopy = (
  // biome-ignore lint/suspicious/noExplicitAny: legacy intake question shape
  question: any,
  // biome-ignore lint/suspicious/noExplicitAny: legacy intake response shape
  response: any,
) => {
  if (!response) {
    if (question.type === "MULTI_SELECT") {
      return [];
    }
    return "";
  }
  switch (question.type) {
    case "SIGNATURE":
      return response;
    case "MULTI_SELECT": {
      const choices = (
        question.questionAttributes as { choices: IntakeQuestionChoice[] }
      ).choices;
      return choices
        .filter((c) => (response as string[]).includes(c.id))
        .map((c) => c.value);
    }
    case "SINGLE_SELECT": {
      const choices = (
        question.questionAttributes as { choices: IntakeQuestionChoice[] }
      ).choices;

      return (
        choices.find((c) => response.toString().includes(c.id))?.value ?? ""
      );
    }
    case "LIST": {
      if (typeof response === "string" && response.includes("[")) {
        const json = JSON.parse(response);
        return (json as IntakeListEntry[]).map((item) => item.value).join(", ");
      }
      if (Array.isArray(response)) {
        return response.map((item) => item.value).join(", ");
      }
      return response as string;
    }
    case "CLINICAL_PROVIDER": {
      if (typeof response === "string") {
        const parsedValue = JSON.parse(response) as ClinicalProviderFormData;
        if (parsedValue.name === "" && parsedValue.phone === "") {
          return "N/A";
        }
        const parts = [
          parsedValue.name,
          parsedValue.address.streetAddress,
          parsedValue.address.city,
          parsedValue.address.state,
          parsedValue.address.zip,
          parsedValue.phone,
        ].filter(Boolean);
        return parts.join(", ");
      }
      if (typeof response === "object") {
        if (response.name === "" && response.phone === "") {
          return "N/A";
        }
        const parts = [
          response.name,
          response.address.streetAddress,
          response.address.city,
          response.address.state,
          response.address.zip,
          response.phone,
        ].filter(Boolean);
        return parts.join(", ");
      }
      return response;
    }
    default:
      return response as string;
  }
};

/**
 * The actual PDF that is created
 * @param param0
 * @returns
 */
export const IntakeModalPdf = ({
  consumer,
  formName,
  sectionedQuestions,
  defaultValues,
  userLocale,
  timeCreated,
}: {
  consumer: ConsumerData;
  formName: string;
  sectionedQuestions: {
    section: string;
    // biome-ignore lint/suspicious/noExplicitAny: legacy intake question shape
    questions: any[];
  }[];
  // biome-ignore lint/suspicious/noExplicitAny: legacy intake response shape
  defaultValues: Record<number, any>;
  userLocale: string;
  timeCreated: string | undefined;
}) => {
  const useNoto = userLocale.includes("zh");
  return (
    <Document title={formName} key={formName}>
      <Page size="LETTER" style={styles.page} key={formName} dpi={600}>
        <PageHeader
          consumer={consumer}
          formName={formName}
          timeCreated={timeCreated}
        />
        {(() => {
          // Render sections
          return sectionedQuestions.map((sections) => {
            if (sections.questions.length === 0) return null;

            return (
              <View key={sections.section} style={{ marginBottom: 10 }}>
                <View>
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: useNoto ? "noto" : "Suisse",
                      marginBottom: 5,
                    }}
                  >
                    {sections.section}
                  </Text>
                </View>
                {sections.questions.map((question, i) => {
                  const response = formatResponseForCopy(
                    question,
                    defaultValues[question.id],
                  );

                  return (
                    ((response !== null && response !== undefined) ||
                      question.questionText
                        .toLowerCase()
                        .includes("total score")) && (
                      <View
                        key={question.id}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        {i > 0 && <View style={styles.divider} />}

                        <View
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                          }}
                          wrap={question.type === "SIGNATURE"}
                        >
                          <Text
                            style={{
                              fontSize: 9,
                              fontFamily: useNoto ? "noto" : "Suisse",
                              fontWeight: 400,
                              flex: 2,
                              marginVertical: 5,
                            }}
                          >
                            {question.questionText}
                          </Text>
                          {question.type === "SIGNATURE" ? (
                            <View
                              style={{
                                flex: 3,
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                height: "100%",
                                gap: 5,
                                marginLeft: 12,
                              }}
                            >
                              {question.questionAttributes?.blurb && (
                                <View
                                  style={{
                                    width: "100%",
                                    marginBottom: 10,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                  }}
                                  wrap
                                >
                                  {parseHtmlToTextComponents(
                                    question.questionAttributes?.blurb,
                                    useNoto,
                                  )}
                                </View>
                              )}
                              <View
                                style={{
                                  width: "100%",
                                  flexDirection: "row",
                                  justifyContent: "flex-start",
                                  gap: 10,
                                  alignItems: "baseline",
                                }}
                              >
                                <Text
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 300,
                                    fontFamily: "Suisse",
                                  }}
                                >
                                  Signature
                                </Text>
                                <View
                                  style={
                                    response.signature?.dataUrl
                                      ? styles.drawnSignatureContainer
                                      : styles.typedSignatureContainer
                                  }
                                >
                                  {response.signature?.dataUrl ? (
                                    <Image
                                      src={response.signature?.dataUrl}
                                      style={{
                                        marginLeft: 10,
                                        height: "auto",
                                        objectFit: "cover",
                                        width: "100%",
                                      }}
                                    />
                                  ) : (
                                    <Text
                                      style={[
                                        {
                                          fontSize: 9,
                                          fontWeight: 300,
                                          marginLeft: 15,
                                          fontFamily:
                                            response.signature?.fontStyle ===
                                            "fancy"
                                              ? "delafield"
                                              : response.signature
                                                    ?.fontStyle === "print"
                                                ? "vujahday"
                                                : "madi",
                                        },
                                      ]}
                                    >
                                      {response.signature?.text ?? ""}
                                    </Text>
                                  )}
                                  <View style={styles.signatureLine} />
                                </View>

                                <Text
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 300,
                                    fontFamily: useNoto ? "noto" : "Suisse",
                                    marginLeft: 15,
                                  }}
                                >
                                  Date
                                </Text>
                                <View style={styles.dateContainer}>
                                  {response?.date && (
                                    <Text style={styles.dateText}>
                                      {response?.date}
                                    </Text>
                                  )}
                                  <View style={styles.dateLine} />
                                </View>
                              </View>
                            </View>
                          ) : question.type === "SINGLE_SELECT" ? (
                            <View
                              style={{
                                flex: 3,
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                height: "100%",
                                gap: 5,
                                marginLeft: 12,
                              }}
                            >
                              {question.questionAttributes?.choices.map(
                                (choice) => (
                                  <View
                                    key={choice.id}
                                    style={{
                                      borderWidth: 1,
                                      borderColor:
                                        choice.value === response
                                          ? "#000"
                                          : "#e1e1e1",
                                      paddingHorizontal: 10,
                                      paddingVertical: 3,
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontFamily: "Suisse",
                                        fontSize: 8,
                                        fontWeight:
                                          choice.value === response ? 500 : 300,
                                      }}
                                    >
                                      {choice.value}
                                    </Text>
                                  </View>
                                ),
                              )}
                            </View>
                          ) : question.type === "MULTI_SELECT" ? (
                            <View
                              style={{
                                flex: 3,
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                height: "100%",
                                gap: 5,
                                marginLeft: 12,
                              }}
                            >
                              {response.map((response) => (
                                <View
                                  key={response}
                                  style={{
                                    borderWidth: 1,
                                    borderColor: "#000",
                                    paddingHorizontal: 10,
                                    paddingVertical: 3,
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontFamily: useNoto ? "noto" : "Suisse",
                                      fontSize: 8,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {response}
                                  </Text>
                                </View>
                              ))}
                            </View>
                          ) : question.questionText
                              .toLowerCase()
                              .includes("total score") ? (
                            <View
                              style={{
                                flex: 3,
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                height: "100%",
                                gap: 5,
                                marginLeft: 12,
                              }}
                            >
                              <View style={styles.totalScoreLine} />
                            </View>
                          ) : (
                            <Text
                              style={{
                                marginLeft: 12,
                                fontFamily: "noto",
                                fontSize: 9,
                                flex: 3,
                                marginVertical: 5,
                              }}
                            >
                              {response}
                            </Text>
                          )}
                        </View>
                      </View>
                    )
                  );
                })}
              </View>
            );
          });
        })()}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
        <View
          style={{
            display: "flex",
            flexDirection: "column",
            position: "absolute",
            bottom: 30,
            left: 30,
            fontSize: 8,
            color: "#666",
          }}
          fixed
        >
          <Text style={styles.footer}>
            Printable intake form by Superscript
          </Text>
          <Text style={styles.footer}>
            Questions about this form? Contact Superscript at
            support@superscript.nyc
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export const generateIntakeFormPdfPC = async (
  consumer: ConsumerData,
  formName: string,
  userLocale: LanguageCode,
  sectionedQuestions: {
    section: string;
    // biome-ignore lint/suspicious/noExplicitAny: legacy intake question shape
    questions: any[];
  }[],
  // biome-ignore lint/suspicious/noExplicitAny: legacy intake response shape
  defaultValues: Record<number, any>,
  timeCreated: string | undefined,
): Promise<Blob> => {
  const pdfDoc = (
    <IntakeModalPdf
      consumer={consumer}
      formName={formName}
      sectionedQuestions={sectionedQuestions}
      defaultValues={defaultValues}
      userLocale={userLocale}
      timeCreated={timeCreated}
    />
  );

  try {
    const pdfInstance = await pdf(
      pdfDoc ?? <Document title={formName} key={formName} />,
    ).toBlob();
    return pdfInstance;
  } catch (error) {
    console.error("Error creating PDF instance:", error);
    await new Promise((resolve) => setTimeout(resolve, 100));
    const retryInstance = await pdf(pdfDoc).toBlob();
    if (!retryInstance) {
      throw new Error("Failed to create PDF instance after retry");
    }
    return retryInstance;
  }
};
